import Service from '@ember/service';
import { captureException, withScope } from '@sentry/ember';
import { ReadContextualizedEntityConfigurationRequest, ReadEntityConfigurationRequest, UpsertEntityConfigurationRequest } from '@square/dashboard-protos/models/squareup/channelcontrols/configuration/service';
import FulfillmentConfigurationClient from '@dashboard/shared-ui/clients/fulfillment-configuration-rpc';
export class FulfillmentConfigurationInputError extends Error {}
export default class FulfillmentConfigurationApi extends Service {
  async readContextualizedEntityConfiguration(context) {
    this.assertEntityIdentifier(context);
    const request = new ReadContextualizedEntityConfigurationRequest.Builder().context(context).build();
    try {
      const response = await FulfillmentConfigurationClient.readContextualizedEntityConfiguration(request);
      this.assertErrors(response);
      this.assertUndefined(response.resolvedConfiguration, 'ReadContextualizedEntityConfiguration.resolvedConfiguration');
      return response.resolvedConfiguration;
    } catch (e) {
      this.captureException(request.toJSON(), e);
      throw e;
    }
  }
  async readEntityConfiguration(entity) {
    this.assertEntityIdentifier(entity);
    const request = new ReadEntityConfigurationRequest.Builder().entity(entity).build();
    try {
      const response = await FulfillmentConfigurationClient.readEntityConfiguration(request);
      this.assertErrors(response);
      this.assertUndefined(response.configuration, 'ReadEntityConfiguration.configuration');
      return response.configuration;
    } catch (e) {
      this.captureException(request.toJSON(), e);
      throw e;
    }
  }
  async upsertEntityConfiguration(configuration) {
    this.assertFulfillmentConfiguration(configuration);
    const request = new UpsertEntityConfigurationRequest.Builder().configuration(configuration).build();
    try {
      const response = await FulfillmentConfigurationClient.upsertEntityConfiguration(request);
      this.assertErrors(response);
      this.assertUndefined(response.configuration, 'UpsertEntityConfiguration.configuration');
      return response.configuration;
    } catch (e) {
      this.captureException(request.toJSON(), e);
      throw e;
    }
  }
  assertEntityIdentifier(entity) {
    if (!entity.merchantId) {
      throw new FulfillmentConfigurationInputError('A merchantId is required to make a FulfillmentConfiguration request');
    }
  }
  assertFulfillmentConfiguration(configuration) {
    if (!configuration.entity) {
      throw new FulfillmentConfigurationInputError('An entity is required to make an UpsertEntityConfiguration request');
    }
    this.assertEntityIdentifier(configuration.entity);
  }
  assertErrors(response) {
    const errors = response?.errors;
    if (errors && errors.length > 0) {
      const errorsString = errors.map(error => {
        const code = error.code ? `[${error.code}]` : '';
        const category = error.category ? `[${error.category}]` : '';
        return `${code}${category} ${error.detail}`.trim();
      });
      throw new Error(errorsString.join('\n'));
    }
  }
  assertUndefined(value, propertyName) {
    if (value === undefined) {
      throw new Error(`Undefined ${propertyName ?? 'value'}`);
    }
  }
  captureException(request, e) {
    const fulfillmentConfigurationApiContext = {
      request: JSON.stringify(request)
    };
    withScope(scope => {
      scope.setContext('fulfillmentConfigurationApiContext', fulfillmentConfigurationApiContext);
      captureException(e);
    });
  }
}